import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Plus, CheckCircle2, MessageCircle, Truck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { CONTACT_TEXT, waUrl } from "../waHelper";

const statusColor = {
  New: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  "In Review": "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  "Picked Up": "bg-orange-500/15 text-orange-400 border-orange-500/30",
  Delivered: "bg-green-500/15 text-green-400 border-green-500/30",
};

export default function CustomerDashboard() {
  const { user, settings, corridors } = useApp();
  const nav = useNavigate();
  const [mine, setMine] = useState([]);
  useEffect(() => { api.mySubmissions().then(setMine).catch(() => setMine([])); }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 py-12">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div>
          <h1 className="font-display font-bold text-3xl">Hi, {user?.name} 👋</h1>
          <p className="text-neutral-600 mt-1">Track and manage all your parcel requests.</p>
        </div>
        <Button className="orange-gradient text-white border-0 gap-2 hover:opacity-90" onClick={() => nav("/send")}><Plus className="w-4 h-4" /> New request</Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-5 mb-10">
        {[
          { icon: Package, label: "Total requests", value: mine.length },
          { icon: Truck, label: "In transit", value: mine.filter((m) => m.status === "Picked Up").length },
          { icon: CheckCircle2, label: "Delivered", value: mine.filter((m) => m.status === "Delivered").length },
        ].map((s) => (
          <div key={s.label} className="glass rounded-2xl p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl orange-gradient grid place-items-center"><s.icon className="w-6 h-6 text-white" /></div>
            <div><p className="font-display font-bold text-2xl">{s.value}</p><p className="text-sm text-neutral-600">{s.label}</p></div>
          </div>
        ))}
      </div>

      <h2 className="font-display font-semibold text-xl mb-4">My requests</h2>
      {mine.length ? (
        <div className="space-y-4">
          {mine.map((s) => {
            const route = corridors.find((c) => c.id === s.route);
            return (
              <div key={s.id} className="glass rounded-2xl p-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-2xl mb-1">{route?.from} → {route?.to}</div>
                    <p className="font-display font-semibold">{route?.name || s.route}</p>
                    <p className="text-sm text-neutral-600 mt-1">{s.serviceType ? `${s.serviceType} · ` : ""}To {s.receiverName} · {s.boxes?.length ? `${s.boxes.length} box${s.boxes.length > 1 ? "es" : ""}` : `${s.weight}kg`} · {new Date(s.createdAt).toLocaleDateString()}</p>
                  </div>
                  <Badge variant="outline" className={statusColor[s.status] || statusColor.New}>{s.status}</Badge>
                </div>
                {s.reply && (
                  <div className="mt-4 glass rounded-xl p-4 border-orange-500/20">
                    <p className="text-xs text-orange-400 font-medium flex items-center gap-1.5 mb-1"><MessageCircle className="w-3.5 h-3.5" /> Reply from our team</p>
                    <p className="text-sm text-neutral-700">{s.reply}</p>
                  </div>
                )}
                <a href={waUrl(settings.whatsapp, CONTACT_TEXT)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-orange-500 hover:text-orange-400 mt-4"><MessageCircle className="w-4 h-4" /> Message about this parcel</a>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="glass rounded-2xl p-12 text-center">
          <Package className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-600">You haven't sent any parcels yet.</p>
          <Button className="orange-gradient text-white border-0 mt-5 gap-2 hover:opacity-90" onClick={() => nav("/send")}><Plus className="w-4 h-4" /> Send your first parcel</Button>
        </div>
      )}
    </div>
  );
}
